import React, { useState } from 'react';
import { CircularProgress, Stack, Typography } from '@mui/material';
import { TimeboxSessionType } from '@demind-inc/core';
import clsx from 'clsx';

import { TimeboxPrefItemState } from '../reducer';
import { translateMinToHHhMMmString } from '../../../../data-access';
import './SchedulerTimeboxesPref.scss';
import { DurationPickerModal } from '../../common';
import { TIMEBOX_PREFS_ITEMS_INFO } from './constants';
import { ChevronRight } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface TimboxPrefItem {
  duration: {
    durationMin: number;
    minimumDurationMin: number;
    maximumDurationMin: number;
  };
}

interface SchedulerTimeboxesPrefProps {
  timeboxes: TimeboxPrefItemState;
  isLoading?: boolean;
  fontSize?: 'small' | 'medium' | 'large';
  onTimeboxChange: (id: TimeboxSessionType, pref: TimboxPrefItem) => void;
  className?: string;
  hasConfigured?: boolean;
  isInSettingsModal?: boolean;
}

interface EditingTimebox {
  id: TimeboxSessionType;
  title: string;
  pref: TimboxPrefItem;
}

export const SchedulerTimeboxesPref: React.FC<SchedulerTimeboxesPrefProps> = ({
  timeboxes,
  isLoading,
  onTimeboxChange,
  className,
  fontSize,
  hasConfigured,
  isInSettingsModal = false,
}) => {
  const { t } = useTranslation();
  const [editingTimebox, setEditingTimebox] = useState<EditingTimebox | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleConfirmDurationPicker = (hours: number, minutes: number) => {
    if (!editingTimebox) {
      return;
    }
    onTimeboxChange(editingTimebox.id, {
      ...editingTimebox.pref,
      duration: {
        ...editingTimebox.pref.duration,
        durationMin: hours * 60 + minutes,
      },
    });
    setEditingTimebox(null);
    setIsModalOpen(false);
  };

  const handleClickTimebox = (id: TimeboxSessionType) => {
    setEditingTimebox({
      id,
      title: t(TIMEBOX_PREFS_ITEMS_INFO[id].titleKey),
      pref: timeboxes[id],
    });
    setIsModalOpen(true);
  };

  const handleCloseDurationPicker = () => {
    setEditingTimebox(null);
    setIsModalOpen(false);
  };

  const renderPreferenceViewItem = (
    timeboxId: TimeboxSessionType,
    timebox: TimboxPrefItem,
    title: string
  ) => {
    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        className="scheduler-timeboxes-pref__list-item-ff"
        borderRadius={4}
        p={2}
        onClick={() => handleClickTimebox(timeboxId as TimeboxSessionType)}
      >
        <Typography variant="body2" className="scheduler-timeboxes-pref__list-item-ff__title">
          {title}
        </Typography>
        <Stack direction="row" alignItems="center">
          <Typography
            variant={isInSettingsModal ? 'subtitle1' : 'caption'}
            className="scheduler-timeboxes-pref__list-item-ff__duration"
          >
            {translateMinToHHhMMmString(timebox.duration.durationMin)}
          </Typography>
          <ChevronRight className="scheduler-timeboxes-pref__list-item-ff__icon" />
        </Stack>
      </Stack>
    );
  };
  return (
    <div
      className={clsx('scheduler-timeboxes-pref', className, {
        'scheduler-timeboxes-pref--small': fontSize === 'small',
        'scheduler-timeboxes-pref--has-configured': hasConfigured,
        'scheduler-timeboxes-pref--in-settings-modal': isInSettingsModal,
      })}
    >
      <div className={clsx('scheduler-timeboxes-pref__list', 'scheduler-timeboxes-pref__list--ff')}>
        {isLoading ? (
          <div className="scheduler-timeboxes-pref__loading">
            <CircularProgress size={16} />
          </div>
        ) : (
          Object.entries(timeboxes).map(([timeboxId, timebox]) => {
            const timeboxInfo = TIMEBOX_PREFS_ITEMS_INFO[timeboxId as TimeboxSessionType];
            return renderPreferenceViewItem(
              timeboxId as TimeboxSessionType,
              timebox,
              t(timeboxInfo.titleKey)
            );
          })
        )}
      </div>

      {editingTimebox && (
        <DurationPickerModal
          isOpen={isModalOpen}
          onClose={handleCloseDurationPicker}
          onSave={(hours, minutes) => {
            handleConfirmDurationPicker(hours, minutes);
          }}
          title={t('settings.aiAssistant.setDurationOf', { title: editingTimebox.title })}
          initialDurationMin={editingTimebox.pref.duration.durationMin}
        />
      )}
    </div>
  );
};

export default SchedulerTimeboxesPref;
